import React, { useState, useEffect } from 'react';
import { ContactWithDetails, Contact, Phone, Email, ContactsAddresses } from './types';
import { FormErrors } from '../../../types';
import { contactsApi, phonesApi, emailsApi, contactsAddressesApi } from './services/contactApi';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { useDirtyChangeReport } from '../../../hooks/useDirtyChangeReport';
import { useLoadingState } from '../../../hooks/useLoadingState';
import { validateRequired, validatePhoneNumber, validateEmail } from '../../../utils/validation';
import PhoneSection from './PhoneSection';
import EmailSection from './EmailSection';
import AddressSection from './AddressSection';
import ContactInfoSection from './ContactInfoSection';
import ContactNavigateConfirmDialog from './ContactNavigateConfirmDialog';
import { LoadingBar } from '../../../components/common/LoadingBar';
import { ActionButton } from '../../../components/common/ActionButton';
import { categoryService } from '../category/services/categoryService';
import '../../../components/common/CRMForm.css';
import './ContactForm.css';

type NavigateDirection = 'previous' | 'next';

export interface ContactSaveResult {
  contactId: number;
  /** When true, keep editing this contact after save (Update). */
  staySelected: boolean;
}

interface ContactFormProps {
  contact: ContactWithDetails | null;
  /** Current filtered/paginated list used for previous/next navigation. */
  filteredContacts?: ContactWithDetails[];
  onSave: (result: ContactSaveResult) => void | Promise<void>;
  onCancel: () => void;
  onNavigate?: (contact: ContactWithDetails) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const ContactForm: React.FC<ContactFormProps> = ({
  contact,
  filteredContacts = [],
  onSave,
  onCancel,
  onNavigate,
  onDirtyChange
}) => {
  const isEditing = !!contact;
  
  // Form state
  const [formData, setFormData] = useState({
    typeId: 0, // No selection - user must choose
    firstName: '',
    lastName: '',
    businessName: '',
    defaultCategoryId: 0 as number,
    notes: '',
    includeInExport: false,
    isPreferred: false
  });

  const [phones, setPhones] = useState<Phone[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [contactsAddresses, setContactsAddresses] = useState<ContactsAddresses[]>([]);
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState<NavigateDirection | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);
  
  // Enable access checkbox state
  const [enableAccess, setEnableAccess] = useState(false);
  const [initialEnableAccess, setInitialEnableAccess] = useState(false);
  const [categoryName, setCategoryName] = useState<string | null>(null);
  
  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    phones: !isEditing, // Collapsed when editing, expanded when creating
    emails: !isEditing, // Collapsed when editing, expanded when creating
    addresses: !isEditing // Collapsed when editing, expanded when creating
  });

  // Initialize form data
  useEffect(() => {
    if (contact) {
      setFormData({
        typeId: contact.typeId,
        firstName: contact.firstName,
        lastName: contact.lastName,
        businessName: contact.businessName,
        defaultCategoryId: contact.defaultCategoryId ? Number(contact.defaultCategoryId) : 0,
        notes: contact.notes,
        includeInExport: !!contact.includeInExport,
        isPreferred: !!contact.isPreferred
      });
      setPhones(contact.phones || []);
      setEmails(contact.emails || []);
      setContactsAddresses(contact.contactsAddresses || []);
      // Collapse sections when editing
      setExpandedSections({
        phones: false,
        emails: false,
        addresses: false
      });
    } else {
      // Reset for new contact
      setFormData({
        typeId: 0, // No selection - user must choose
        firstName: '',
        lastName: '',
        businessName: '',
        defaultCategoryId: 0,
        notes: '',
        includeInExport: false,
        isPreferred: false
      });
      setPhones([]);
      setEmails([]);
      setContactsAddresses([]);
      // Expand sections when creating
      setExpandedSections({
        phones: true,
        emails: true,
        addresses: true
      });
    }
    setHasChanges(false);
    setErrors({});
    setPendingNavigate(null);
    
    // Check if access is enabled for this contact
    if (contact && contact.id) {
      contactsApi.checkAccessEnabled(contact.id)
        .then(enabled => {
          setEnableAccess(enabled);
          setInitialEnableAccess(enabled);
        })
        .catch(error => {
          console.error('Error checking access status:', error);
          setEnableAccess(false);
          setInitialEnableAccess(false);
        });
    } else {
      setEnableAccess(false);
      setInitialEnableAccess(false);
    }
  }, [contact]);

  // Fetch category name when typeId changes
  useEffect(() => {
    const fetchCategoryName = async () => {
      if (formData.typeId > 0) {
        try {
          const categories = await categoryService.getCategoriesByType('contact');
          const category = categories.find(cat => cat.id === formData.typeId);
          const newCategoryName = category?.name || null;
          setCategoryName(newCategoryName);
          
          // Uncheck enableAccess if category is no longer Tenant or Client
          if (enableAccess && newCategoryName !== 'Tenant' && newCategoryName !== 'Client') {
            setEnableAccess(false);
            setInitialEnableAccess(false);
          }
        } catch (error) {
          console.error('Error fetching category name:', error);
          setCategoryName(null);
          if (enableAccess) {
            setEnableAccess(false);
            setInitialEnableAccess(false);
          }
        }
      } else {
        setCategoryName(null);
        if (enableAccess) {
          setEnableAccess(false);
          setInitialEnableAccess(false);
        }
      }
    };

    fetchCategoryName();
  }, [formData.typeId]);

  // Determine if checkbox should be visible
  const shouldShowCheckbox = categoryName === 'Tenant' || categoryName === 'Client';

  // Determine if checkbox should be enabled (primary email exists)
  const primaryEmail = emails.find(email => email.isPrimary === true);
  const canEnableAccess = shouldShowCheckbox && !!primaryEmail && !!primaryEmail.emailAddress;

  // Track changes
  useEffect(() => {
    if (isEditing && contact) {
      const savedDefaultCat = contact.defaultCategoryId ? Number(contact.defaultCategoryId) : 0;
      const hasContactChanges = 
        formData.typeId !== contact.typeId ||
        formData.firstName !== contact.firstName ||
        formData.lastName !== contact.lastName ||
        formData.businessName !== contact.businessName ||
        formData.defaultCategoryId !== savedDefaultCat ||
        formData.notes !== contact.notes ||
        formData.includeInExport !== !!contact.includeInExport ||
        formData.isPreferred !== !!contact.isPreferred;
      
      const hasPhoneChanges = JSON.stringify(phones) !== JSON.stringify(contact.phones || []);
      const hasEmailChanges = JSON.stringify(emails) !== JSON.stringify(contact.emails || []);
      const hasAddressChanges = JSON.stringify(contactsAddresses) !== JSON.stringify(contact.contactsAddresses || []);
      
      setHasChanges(hasContactChanges || hasPhoneChanges || hasEmailChanges || hasAddressChanges);
    } else if (!isEditing) {
      const hasAnyData = 
        formData.firstName.trim() !== '' ||
        formData.lastName.trim() !== '' ||
        formData.businessName.trim() !== '' ||
        formData.defaultCategoryId > 0 ||
        formData.notes.trim() !== '' ||
        formData.includeInExport ||
        formData.isPreferred ||
        phones.length > 0 ||
        emails.length > 0 ||
        contactsAddresses.length > 0;
      
      setHasChanges(hasAnyData);
    }
  }, [formData, phones, emails, contactsAddresses, isEditing, contact]);

  const { confirmNavigation } = useUnsavedChanges({ hasChanges });
  useDirtyChangeReport(hasChanges, onDirtyChange);

  const handleInputChange = (field: string, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate contact type selection
    if (formData.typeId === 0) {
      newErrors.type = 'Contact type is required';
    }

    const hasFirstName = formData.firstName?.trim() !== '';
    const hasLastName = formData.lastName?.trim() !== '';
    const hasBusinessName = formData.businessName?.trim() !== '';
    if (!hasFirstName && !hasLastName && !hasBusinessName) {
      newErrors.firstName = 'At least one of first name, last name, or business name is required';
    }

    // Validate phones - each phone must have a valid typeId and phone number
    const phoneErrors: string[] = [];
    phones.forEach((phone, index) => {
      if (phone.typeId === 0) {
        phoneErrors.push(`Phone ${index + 1}: Please select a phone type`);
      }
      if (!phone.phoneNumber.trim()) {
        phoneErrors.push(`Phone ${index + 1}: Phone number is required`);
      } else {
        // Use the same validation as PhoneSection
        const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
        if (!phoneRegex.test(phone.phoneNumber)) {
          phoneErrors.push(`Phone ${index + 1}: Please enter a valid phone number`);
        }
      }
    });
    
    if (phoneErrors.length > 0) {
      newErrors.phones = phoneErrors.join(', ');
    }

    // Validate emails - each email must have a valid typeId and email address  
    const emailErrors: string[] = [];
    emails.forEach((email, index) => {
      if (email.typeId === 0) {
        emailErrors.push(`Email ${index + 1}: Please select an email type`);
      }
      if (!email.emailAddress.trim()) {
        emailErrors.push(`Email ${index + 1}: Email address is required`);
      } else {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.emailAddress)) {
          emailErrors.push(`Email ${index + 1}: Please enter a valid email address`);
        }
      }
    });
    
    if (emailErrors.length > 0) {
      newErrors.emails = emailErrors.join(', ');
    }

    // Validate addresses - each address must have typeId and addressId (selected via search)
    const addressErrors: string[] = [];
    contactsAddresses.forEach((contactsAddress, index) => {
      if (contactsAddress.typeId === 0) {
        addressErrors.push(`Address ${index + 1}: Please select an address type`);
      }
      if (!contactsAddress.addressId || contactsAddress.addressId === 0) {
        addressErrors.push(`Address ${index + 1}: Please search and select an address`);
      }
    });
    
    if (addressErrors.length > 0) {
      newErrors.addresses = addressErrors.join(', ');
    }

    // Check if at least one address is marked as primary
    if (contactsAddresses.length > 0 && !contactsAddresses.some(a => a.isPrimary)) {
      newErrors.addresses = (newErrors.addresses ? newErrors.addresses + ', ' : '') + 'Please mark one address as primary';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (options?: { staySelected?: boolean }): Promise<boolean> => {
    if (!validateForm()) return false;

    const staySelected = options?.staySelected ?? isEditing;

    setLoading(true);
    startLoading('save-contact', isEditing ? 'Updating contact...' : 'Creating contact...');
    try {
      let savedContact: Contact;
      
      if (isEditing && contact) {
        savedContact = await contactsApi.update(contact.id, formData);
      } else {
        savedContact = await contactsApi.create(formData);
      }

      // Save phones, emails, and addresses if contact was created/updated successfully
      await Promise.all([
        savePhones(savedContact.id),
        saveEmails(savedContact.id),
        saveContactsAddresses(savedContact.id)
      ]);

      // Handle access enable/disable after emails are saved (so primary email exists in database)
      if (enableAccess !== initialEnableAccess) {
        try {
          if (enableAccess) {
            await contactsApi.enableAccess(savedContact.id);
          } else {
            await contactsApi.disableAccess(savedContact.id);
          }
          setInitialEnableAccess(enableAccess);
        } catch (error: any) {
          console.error('Error updating contact access:', error);
          const errorMessage = error?.response?.data?.error || error?.message || 'Failed to update access';
          setErrors({ general: errorMessage });
          return false;
        }
      }

      setHasChanges(false);
      await onSave({ contactId: savedContact.id, staySelected });
      return true;
    } catch (error: any) {
      console.error('Error saving contact:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to save contact. Please try again.';
      setErrors({ general: errorMessage });
      return false;
    } finally {
      setLoading(false);
      stopLoading('save-contact');
    }
  };

  const savePhones = async (contactId: number) => {
    // Validate phones before saving
    const validPhones = phones.filter(phone => 
      phone.typeId > 0 && 
      phone.phoneNumber.trim() !== '' && 
      validatePhoneNumber(phone.phoneNumber)
    );

    if (isEditing && contact) {
      const originalPhones = contact.phones || [];
      
      // Find phones to delete (exist in original but not in current)
      const phonesToDelete = originalPhones.filter(originalPhone => 
        !validPhones.some(currentPhone => currentPhone.id === originalPhone.id)
      );
      
      // Find phones to update (exist in both but have changes)
      const phonesToUpdate = validPhones.filter(currentPhone => {
        if (currentPhone.id === 0) return false; // Skip new phones (id === 0)
        const originalPhone = originalPhones.find(p => p.id === currentPhone.id);
        if (!originalPhone) return false;
        
        return (
          currentPhone.typeId !== originalPhone.typeId ||
          currentPhone.phoneNumber !== originalPhone.phoneNumber ||
          currentPhone.isPrimary !== originalPhone.isPrimary
        );
      });
      
      // Find phones to create (new phones with id === 0)
      const phonesToCreate = validPhones.filter(phone => phone.id === 0);
      
      // Execute operations in parallel
      await Promise.all([
        // Delete removed phones
        ...phonesToDelete.map(phone => phonesApi.delete(phone.id)),
        
        // Update modified phones
        ...phonesToUpdate.map(phone => phonesApi.update(phone.id, {
          typeId: phone.typeId,
          phoneNumber: phone.phoneNumber,
          isPrimary: phone.isPrimary
        })),
        
        // Create new phones
        ...phonesToCreate.map(phone => phonesApi.create(contactId, {
          typeId: phone.typeId,
          phoneNumber: phone.phoneNumber,
          isPrimary: phone.isPrimary
        }))
      ]);
    } else {
      // For new contacts, just create all valid phones
      await Promise.all(
        validPhones.map(phone => 
          phonesApi.create(contactId, {
            typeId: phone.typeId,
            phoneNumber: phone.phoneNumber,
            isPrimary: phone.isPrimary
          })
        )
      );
    }
  };

  const saveEmails = async (contactId: number) => {
    // Validate emails before saving
    const validEmails = emails.filter(email => 
      email.typeId > 0 && 
      email.emailAddress.trim() !== '' && 
      validateEmail(email.emailAddress)
    );

    if (isEditing && contact) {
      const originalEmails = contact.emails || [];
      
      // Find emails to delete (exist in original but not in current)
      const emailsToDelete = originalEmails.filter(originalEmail => 
        !validEmails.some(currentEmail => currentEmail.id === originalEmail.id)
      );
      
      // Find emails to update (exist in both but have changes)
      const emailsToUpdate = validEmails.filter(currentEmail => {
        if (currentEmail.id === 0) return false; // Skip new emails (id === 0)
        const originalEmail = originalEmails.find(e => e.id === currentEmail.id);
        if (!originalEmail) return false;
        
        return (
          currentEmail.typeId !== originalEmail.typeId ||
          currentEmail.emailAddress !== originalEmail.emailAddress ||
          currentEmail.isPrimary !== originalEmail.isPrimary
        );
      });
      
      // Find emails to create (new emails with id === 0)
      const emailsToCreate = validEmails.filter(email => email.id === 0);
      
      // Execute operations in parallel
      await Promise.all([
        // Delete removed emails
        ...emailsToDelete.map(email => emailsApi.delete(email.id)),
        
        // Update modified emails
        ...emailsToUpdate.map(email => emailsApi.update(email.id, {
          typeId: email.typeId,
          emailAddress: email.emailAddress,
          isPrimary: email.isPrimary
        })),
        
        // Create new emails
        ...emailsToCreate.map(email => emailsApi.create(contactId, {
          typeId: email.typeId,
          emailAddress: email.emailAddress,
          isPrimary: email.isPrimary
        }))
      ]);
    } else {
      // For new contacts, just create all valid emails
      await Promise.all(
        validEmails.map(email => 
          emailsApi.create(contactId, {
            typeId: email.typeId,
            emailAddress: email.emailAddress,
            isPrimary: email.isPrimary
          })
        )
      );
    }
  };

  const saveContactsAddresses = async (contactId: number) => {
    const validContactsAddresses = contactsAddresses.filter(ca =>
      ca.typeId > 0 && ca.addressId > 0
    );

    if (isEditing && contact) {
      const original = contact.contactsAddresses || [];
      const toDelete = original.filter(o => !validContactsAddresses.some(c => c.id === o.id));
      const toUpdate = validContactsAddresses.filter(c => c.id > 0);
      const toCreate = validContactsAddresses.filter(c => c.id === 0);

      await Promise.all([
        ...toDelete.map(ca => contactsAddressesApi.delete(ca.id)),
        ...toUpdate.flatMap(ca => {
          const orig = original.find(o => o.id === ca.id);
          if (!orig) return [];
          const promises: Promise<unknown>[] = [];
          if (ca.typeId !== orig.typeId) promises.push(contactsAddressesApi.updateType(ca.id, ca.typeId));
          if ((ca.description || '') !== (orig.description || '')) promises.push(contactsAddressesApi.updateDescription(ca.id, ca.description || ''));
          if (ca.isPrimary !== orig.isPrimary && ca.isPrimary) promises.push(contactsAddressesApi.setPrimary(ca.id));
          return promises;
        }),
        ...toCreate.map(ca => contactsAddressesApi.createAssociation({
          contactId,
          addressId: ca.addressId,
          typeId: ca.typeId,
          description: ca.description || '',
          isPrimary: ca.isPrimary
        }))
      ]);
    } else {
      await Promise.all(
        validContactsAddresses.map(ca =>
          contactsAddressesApi.createAssociation({
            contactId,
            addressId: ca.addressId,
            typeId: ca.typeId,
            description: ca.description || '',
            isPrimary: ca.isPrimary
          })
        )
      );
    }
  };

  const currentIndex = contact
    ? filteredContacts.findIndex((item) => item.id === contact.id)
    : -1;
  const previousContact = currentIndex > 0 ? filteredContacts[currentIndex - 1] : null;
  const nextContact =
    currentIndex >= 0 && currentIndex < filteredContacts.length - 1
      ? filteredContacts[currentIndex + 1]
      : null;
  const showFilteredNav = isEditing && filteredContacts.length > 0 && !!onNavigate;

  const navigateToNeighbor = (direction: NavigateDirection) => {
    if (!onNavigate) return;
    const target = direction === 'previous' ? previousContact : nextContact;
    if (target) {
      onNavigate(target);
    }
  };

  const requestNavigate = (direction: NavigateDirection) => {
    const target = direction === 'previous' ? previousContact : nextContact;
    if (!target || !onNavigate) return;

    if (hasChanges) {
      setPendingNavigate(direction);
      return;
    }

    navigateToNeighbor(direction);
  };

  const handleDialogCancel = () => {
    setPendingNavigate(null);
  };

  const handleDialogContinue = () => {
    if (!pendingNavigate) return;
    const direction = pendingNavigate;
    setPendingNavigate(null);
    setHasChanges(false);
    navigateToNeighbor(direction);
  };

  const handleDialogSaveAndContinue = async () => {
    if (!pendingNavigate) return;
    const direction = pendingNavigate;
    const saved = await handleSave({ staySelected: true });
    if (!saved) {
      return;
    }
    setPendingNavigate(null);
    navigateToNeighbor(direction);
  };

  const handleCancel = () => {
    if (hasChanges && !confirmNavigation()) {
      return;
    }
    onCancel();
  };

  return (
    <div className="form">
      <div className="form-header">
        <h2>{isEditing ? 'Edit Contact' : 'Create Contact'}</h2>
        {showFilteredNav && (
          <div className="contact-form-header-nav" role="group" aria-label="Contact list navigation">
            <button
              type="button"
              className="contact-form-nav-btn"
              onClick={() => requestNavigate('previous')}
              disabled={!previousContact || loading}
              aria-label="Previous contact"
            >
              &lt;
            </button>
            <button
              type="button"
              className="contact-form-nav-btn"
              onClick={() => requestNavigate('next')}
              disabled={!nextContact || loading}
              aria-label="Next contact"
            >
              &gt;
            </button>
          </div>
        )}
        {hasChanges && <span className="form-unsaved-indicator">• Unsaved changes</span>}
      </div>

      {errors.general && (
        <div className="app-error-message">{errors.general}</div>
      )}

      <div className="form-content">
        {/* Basic Contact Information */}
        <section className="form-section">
          <h3>Contact Information</h3>
          <ContactInfoSection
            contactData={formData}
            errors={errors}
            onChange={handleInputChange}
            enableAccess={enableAccess}
            onEnableAccessChange={setEnableAccess}
            categoryName={categoryName}
            canEnableAccess={canEnableAccess}
            isTypeDisabled={enableAccess}
          />
        </section>

        {/* Phone Numbers Section */}
        <section className="form-section">
          <div 
            className="form-section-header"
            onClick={() => toggleSection('phones')}
          >
            <h3>Phone Numbers ({phones.length})</h3>
            <span className={`form-section-toggle ${expandedSections.phones ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>
          
          {errors.phones && (
            <div className="app-form-error">{errors.phones}</div>
          )}
          
          {expandedSections.phones && (
            <PhoneSection
              phones={phones}
              onChange={setPhones}
            />
          )}
        </section>

        {/* Email Addresses Section */}
        <section className="form-section">
          <div 
            className="form-section-header"
            onClick={() => toggleSection('emails')}
          >
            <h3>Email Addresses ({emails.length})</h3>
            <span className={`form-section-toggle ${expandedSections.emails ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>
          
          {errors.emails && (
            <div className="app-form-error">{errors.emails}</div>
          )}
          
          {expandedSections.emails && (
            <EmailSection
              emails={emails}
              onChange={setEmails}
            />
          )}
        </section>

        {/* Addresses Section */}
        <section className="form-section">
          <div 
            className="form-section-header"
            onClick={() => toggleSection('addresses')}
          >
            <h3>Addresses ({contactsAddresses.length})</h3>
            <span className={`form-section-toggle ${expandedSections.addresses ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>
          
          {errors.addresses && (
            <div className="app-form-error">{errors.addresses}</div>
          )}
          
          {expandedSections.addresses && (
            <AddressSection
              contactsAddresses={contactsAddresses}
              onChange={setContactsAddresses}
            />
          )}
        </section>
      </div>

      <div className="app-form-actions">
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <ActionButton
          className="app-btn app-btn-primary"
          label={isEditing ? 'Update' : 'Create'}
          busyLabel="Saving..."
          busy={loading}
          disabled={!hasChanges}
          onClick={() => handleSave()}
        />
      </div>
      
      <ContactNavigateConfirmDialog
        isOpen={pendingNavigate !== null}
        saving={loading}
        onCancel={handleDialogCancel}
        onContinue={handleDialogContinue}
        onSaveAndContinue={() => {
          void handleDialogSaveAndContinue();
        }}
      />

      <LoadingBar isLoading={globalLoading} message={loadingMessage} />
    </div>
  );
};

export default ContactForm;
